import { FC, useEffect, useState, useCallback } from 'react'
import {
	Table,
	Button,
	Select,
	Tag,
	Modal,
	Form,
	Input,
	DatePicker,
	Popconfirm,
	message,
	Space,
	Tooltip
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import {
	Equipment,
	EquipmentCondition
} from '../helpers/types/inventory'
import {
	fetchEquipment,
	createEquipment,
	deleteEquipment
} from '../api/inventory'
import { carrierStore } from '../store/carrier'
import { Link } from 'react-router-dom'
import { RNCarriers } from '../helpers/routeNames'

const CONDITION_COLOR: Record<EquipmentCondition, string> = {
	good: 'success',
	worn: 'warning',
	damaged: 'error'
}

const InventoryEquipment: FC = () => {
	const { carriers } = carrierStore()
	const [carrierId, setCarrierId] = useState<number | null>(null)
	const [equipment, setEquipment] = useState<Equipment[]>([])
	const [loading, setLoading] = useState(false)
	const [addOpen, setAddOpen] = useState(false)
	const [saving, setSaving] = useState(false)
	const [form] = Form.useForm()
	const load = useCallback(async (cId: number) => {
		setLoading(true)
		try {
			const data = await fetchEquipment(cId)
			setEquipment(data)
		} catch {
			message.error('Failed to load equipment')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (carrierId) load(carrierId)
		else setEquipment([])
	}, [carrierId, load])

	const handleAdd = async () => {
		if (!carrierId) return
		const values = await form.validateFields()
		setSaving(true)
		try {
			const eq = await createEquipment({
				carrierId,
				...values,
				installedAt: values.installedAt
					? values.installedAt.toISOString()
					: undefined
			})
			setEquipment((prev) => [...prev, eq])
			setAddOpen(false)
			form.resetFields()
		} catch {
			message.error('Error')
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async (id: number) => {
		try {
			await deleteEquipment(id)
			setEquipment((prev) => prev.filter((e) => e.id !== id))
		} catch {
			message.error('Error')
		}
	}

	const columns = [
		{
			title: 'Carrier',
			key: 'carrier',
			render: () => {
				const c = carriers.find((c) => c.id === carrierId)
				return c ? (
					<Link
						to={`${RNCarriers}/${c.id}#inventory-equipment`}>
						{c.name}
					</Link>
				) : (
					'—'
				)
			}
		},
		{
			title: 'Component',
			dataIndex: 'componentType',
			key: 'componentType'
		},
		{
			title: 'Model',
			dataIndex: 'model',
			key: 'model'
		},
		{
			title: 'Serial',
			dataIndex: 'serialNumber',
			key: 'serialNumber',
			render: (v: string | null) => v || '—'
		},
		{
			title: 'Condition',
			dataIndex: 'condition',
			key: 'condition',
			render: (v: EquipmentCondition) => (
				<Tag color={CONDITION_COLOR[v]}>{v}</Tag>
			)
		},
		{
			title: 'Controlled',
			dataIndex: 'isControlled',
			key: 'isControlled',
			render: (v: boolean) => (
				<Tag color={v ? 'purple' : 'default'}>
					{v ? 'Our Device' : '3rd Party'}
				</Tag>
			)
		},
		{
			title: 'Installed',
			dataIndex: 'installedAt',
			key: 'installedAt',
			render: (v: string | null) =>
				v ? new Date(v).toLocaleDateString('ru') : '—'
		},
		{
			title: '',
			key: 'actions',
			width: 60,
			render: (_: any, rec: Equipment) => (
				<Popconfirm
					title="Delete?"
					onConfirm={() => handleDelete(rec.id)}
					okButtonProps={{ danger: true }}>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						type="text"
						danger
					/>
				</Popconfirm>
			)
		}
	]

	return (
		<div>
			<header className="page-header">
				<h1 className="page-h1">Equipment Inventory</h1>
			</header>
			<main>
				<Space
					style={{
						marginBottom: 16,
						display: 'flex',
						flexWrap: 'wrap'
					}}
					size={8}>
					<Select
						placeholder="Select carrier"
						style={{ width: 220 }}
						allowClear
						showSearch
						optionFilterProp="label"
						value={carrierId}
						onChange={(v) => setCarrierId(v ?? null)}
						options={carriers.map((c) => ({
							label: c.name,
							value: c.id
						}))}
					/>
					<Tooltip
						title={
							!carrierId ? 'Select a carrier first' : ''
						}>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							disabled={!carrierId}
							onClick={() => setAddOpen(true)}>
							Add Equipment
						</Button>
					</Tooltip>
				</Space>

				<Table
					dataSource={equipment}
					columns={columns}
					rowKey="id"
					loading={loading}
					pagination={{ pageSize: 20 }}
					locale={{
						emptyText: carrierId
							? 'No equipment'
							: 'Select a carrier to view equipment'
					}}
				/>
			</main>

			<Modal
				title="Add Equipment"
				open={addOpen}
				onCancel={() => setAddOpen(false)}
				onOk={handleAdd}
				confirmLoading={saving}
				okText="Add"
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="componentType"
						label="Component Type"
						rules={[{ required: true }]}>
						<Input placeholder="e.g. control_unit, contactor" />
					</Form.Item>
					<Form.Item name="model" label="Model">
						<Input />
					</Form.Item>
					<Form.Item
						name="serialNumber"
						label="Serial Number">
						<Input />
					</Form.Item>
					<Form.Item
						name="condition"
						label="Condition"
						initialValue="good">
					<Select options={[
						{ value: 'good', label: 'Good' },
						{ value: 'worn', label: 'Worn' },
						{ value: 'damaged', label: 'Damaged' }
					]} />
					</Form.Item>
					<Form.Item
						name="isControlled"
						label="Controlled"
						initialValue={true}>
					<Select options={[
						{ value: true, label: 'Yes — our device' },
						{ value: false, label: 'No — 3rd party' }
					]} />
					</Form.Item>
					<Form.Item
						name="installedAt"
						label="Installed At">
						<DatePicker style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item name="notes" label="Notes">
						<Input.TextArea rows={2} />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	)
}

export default InventoryEquipment
